import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiTV, tokenStore, buildFileUrl } from "@/lib/api";
import { LogOut, Loader2, ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import CrossfadeMedia from "@/components/CrossfadeMedia";

export default function TVPlayer() {
  const navigate = useNavigate();
  const [tv, setTv] = useState(null);
  const [playlist, setPlaylist] = useState(null);
  const [media, setMedia] = useState([]);
  const [nextSchedule, setNextSchedule] = useState(null);
  const [version, setVersion] = useState(() => {
    try {
      const pos = JSON.parse(localStorage.getItem("cc_tv_position") || "null");
      return pos?.version || "";
    } catch {
      return "";
    }
  });
  const [index, setIndex] = useState(() => {
    try {
      const pos = JSON.parse(localStorage.getItem("cc_tv_position") || "null");
      return Number.isFinite(pos?.index) ? pos.index : 0;
    } catch {
      return 0;
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showOverlay, setShowOverlay] = useState(false);
  const overlayTimer = useRef(null);
  const indexRef = useRef(0);
  const mediaRef = useRef([]);
  const syncRef = useRef(null);
  indexRef.current = index;
  mediaRef.current = media;

  // Auth check — if no token, attempt SILENT auto-register so a fresh Fire Stick
  // pairs itself with the platform automatically. Falls back to /tv-login if
  // auto-registration fails (e.g. backend unreachable).
  useEffect(() => {
    if (tokenStore.getTV()) return;
    let cancelled = false;
    (async () => {
      // Stable device fingerprint stored in localStorage (regenerated only if cleared)
      let deviceUid;
      try {
        deviceUid = localStorage.getItem("cc_device_uid");
        if (!deviceUid) {
          const arr = new Uint8Array(20);
          (window.crypto || window.msCrypto).getRandomValues(arr);
          deviceUid = "dev-" + Array.from(arr, (b) => b.toString(16).padStart(2, "0")).join("");
          localStorage.setItem("cc_device_uid", deviceUid);
        }
      } catch {
        deviceUid = "dev-fallback-" + Date.now();
      }
      try {
        const base = process.env.REACT_APP_BACKEND_URL || "";
        const res = await fetch(`${base}/api/tv/auto-register`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            device_uid: deviceUid,
            user_agent: navigator.userAgent.slice(0, 200),
          }),
        });
        if (!res.ok) throw new Error(`auto-register failed (${res.status})`);
        const j = await res.json();
        tokenStore.setTV(j.token);
        if (cancelled) return;
        // Trigger sync immediately
        window.location.reload();
      } catch {
        if (!cancelled) navigate("/tv-login", { replace: true });
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  // Mark <body> while the TV Player is mounted so the global gold-button theme
  // is skipped (the player keeps its cinematic dark glass overlay style).
  useEffect(() => {
    document.body.classList.add("tv-player-active");
    return () => document.body.classList.remove("tv-player-active");
  }, []);

  // ---- Service Worker: aggressive media cache --------------------------------
  // Cuts Supabase bandwidth by ~95% on Fire Sticks. Every immutable media URL
  // is cached locally after the first download, so subsequent loops play from
  // the device's storage with zero egress cost on the Supabase bill.
  // Only the TV Player registers this SW (admin uses fresh data).
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    navigator.serviceWorker.register("/tv-sw.js", { scope: "/" }).catch(() => {
      /* SW registration is best-effort; the app still works without it */
    });
  }, []);

  // ---- Service Worker: aggressive media cache --------------------------------
  // Cuts Supabase bandwidth by ~95% on Fire Sticks. Every immutable media URL
  // is cached locally after the first download, so subsequent loops play from
  // the device's storage with zero egress cost on the Supabase bill.
  // Only the TV Player registers this SW (admin uses fresh data).
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const swUrl = "/tv-sw.js";
    navigator.serviceWorker.register(swUrl, { scope: "/" }).catch(() => {
      /* SW registration is best-effort; the app still works without it */
    });
  }, []);

  // Sync loop: ONE request every 5s + offline cache so the TV never goes black.
  // Playback position is persisted so refresh / leaving the page doesn't restart the playlist.
  useEffect(() => {
    let mounted = true;
    let currentVersion = "";
    const CACHE_KEY = "cc_tv_last_sync";
    const POS_KEY = "cc_tv_position"; // { version, index, ts }

    // Read saved playback position
    let savedIndex = 0;
    try {
      const pos = JSON.parse(localStorage.getItem(POS_KEY) || "null");
      if (pos && pos.version) {
        currentVersion = pos.version;
        savedIndex = Number.isFinite(pos.index) ? pos.index : 0;
      }
    } catch {
      /* ignore */
    }

    // Hydrate from local cache instantly so the TV resumes playing on reload / network drop.
    try {
      const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || "null");
      if (cached && cached.media?.length) {
        const startIdx =
          cached.version && cached.version === currentVersion
            ? Math.min(savedIndex, Math.max(0, cached.media.length - 1))
            : 0;
        // Update refs synchronously so the very first sync() call sees the right values
        mediaRef.current = cached.media;
        indexRef.current = startIdx;
        setTv(cached.tv);
        setPlaylist(cached.playlist);
        setMedia(cached.media);
        setVersion(cached.version || "");
        setIndex(startIdx);
        if (!cached.version || cached.version !== currentVersion) {
          currentVersion = cached.version || "";
        }
        setLoading(false);
      }
    } catch {
      /* ignore */
    }

    const sync = async () => {
      try {
        const { data } = await apiTV.get("/tv/sync");
        if (!mounted) return;
        setTv(data.tv);
        setNextSchedule(data.next_schedule || null);
        setError("");
        if (data.version !== currentVersion) {
          const newMedia = data.media || [];
          // 24/7 channel behavior: keep the current item visible if it still exists.
          // We compute the new index without restarting the playlist.
          let nextIndex = 0;
          // Prefer the freshest in-memory state via the refs (avoid stale closures)
          const playingId = mediaRef.current.length
            ? mediaRef.current[indexRef.current % mediaRef.current.length]?.id
            : null;
          if (playingId) {
            const foundAt = newMedia.findIndex((m) => m.id === playingId);
            if (foundAt >= 0) {
              nextIndex = foundAt; // same item still in the new list - stay on it
            } else if (newMedia.length > 0) {
              // Current item was removed; clamp position so we don't visibly jump to 0.
              nextIndex = Math.min(indexRef.current, newMedia.length - 1);
            }
          } else {
            // Nothing was playing yet — try a saved position from a previous session.
            if (savedIndex > 0 && newMedia.length > savedIndex) {
              nextIndex = savedIndex;
            }
          }
          currentVersion = data.version;
          setVersion(data.version);
          setPlaylist(data.playlist);
          // Update refs synchronously to keep heartbeat & subsequent sync calls coherent.
          mediaRef.current = newMedia;
          indexRef.current = nextIndex;
          setMedia(newMedia);
          setIndex(nextIndex);
          try {
            localStorage.setItem(CACHE_KEY, JSON.stringify(data));
          } catch {
            /* storage full - ignore */
          }
        }
      } catch (e) {
        if (!mounted) return;
        if (e?.response?.status === 401) {
          tokenStore.clearTV();
          localStorage.removeItem(CACHE_KEY);
          localStorage.removeItem(POS_KEY);
          navigate("/tv-login", { replace: true });
        } else {
          // Network/server error - keep playing cached content, retry on next tick
          setError("Reconnecting…");
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };
    syncRef.current = sync;
    sync();
    const id = setInterval(sync, 5000);
    return () => {
      mounted = false;
      syncRef.current = null;
      clearInterval(id);
    };
  }, [navigate]);

  // ---- Real-time push from admin via SSE ----------------------------------
  // EventSource cannot set Authorization headers, so we pass the TV token via
  // the query string. On every change event from the backend we trigger a
  // sync immediately, eliminating the 5s polling lag when an admin saves.
  useEffect(() => {
    const token = tokenStore.getTV();
    if (!token) return;
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const url = `${base}/api/tv/events?token=${encodeURIComponent(token)}`;
    let es;
    let reconnectTimer;
    let stopped = false;

    const connect = () => {
      try {
        es = new EventSource(url);
      } catch {
        return;
      }
      // Generic "sync" handler — any non-refresh event from the backend means
      // some content changed, so re-fetch the current playlist.
      const onAnyChange = () => {
        if (typeof syncRef.current === "function") {
          syncRef.current();
        }
      };
      ["change", "playlist_updated", "playlist_created", "playlist_deleted",
       "tv_updated", "tv_deleted", "tv_assigned",
       "schedule_added", "schedule_deleted"].forEach((name) => {
        es.addEventListener(name, onAnyChange);
      });
      es.addEventListener("refresh", (ev) => {
        // Targeted remote-refresh from admin. The TV id is in the event payload;
        // if it matches us (or no target was given) we hard-reload the page.
        try {
          const data = ev?.data ? JSON.parse(ev.data) : {};
          const meId = (() => {
            try {
              const payload = JSON.parse(atob((tokenStore.getTV() || "").split(".")[1] || ""));
              return payload?.sub;
            } catch {
              return null;
            }
          })();
          if (!data.tv_id || data.tv_id === meId) {
            window.location.reload();
          }
        } catch {
          window.location.reload();
        }
      });
      es.addEventListener("clear_cache", async (ev) => {
        // Admin asked us to wipe the local media cache, then reload.
        try {
          const data = ev?.data ? JSON.parse(ev.data) : {};
          const meId = (() => {
            try {
              const payload = JSON.parse(atob((tokenStore.getTV() || "").split(".")[1] || ""));
              return payload?.sub;
            } catch {
              return null;
            }
          })();
          if (data.tv_id && data.tv_id !== meId) return;
        } catch { /* ignore */ }
        try {
          // Talk to the Service Worker if it's installed
          if (navigator.serviceWorker?.controller) {
            navigator.serviceWorker.controller.postMessage({ type: "PURGE_CACHE" });
          }
          // Also wipe Cache Storage directly so it's guaranteed even without an SW yet
          if (window.caches) {
            const names = await caches.keys();
            await Promise.all(names.map((n) => caches.delete(n)));
          }
        } catch { /* ignore */ }
        window.location.reload();
      });
      es.onerror = () => {
        try { es.close(); } catch { /* ignore */ }
        if (stopped) return;
        // Auto-reconnect with backoff so the TV self-heals after admin pod restart
        reconnectTimer = window.setTimeout(connect, 4000);
      };
    };
    connect();
    return () => {
      stopped = true;
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
      try { es && es.close(); } catch { /* ignore */ }
    };
  }, []);

  // Persist playback position whenever the index advances (so refresh resumes here).
  useEffect(() => {
    if (!version) return;
    try {
      localStorage.setItem(
        "cc_tv_position",
        JSON.stringify({ version, index, ts: Date.now() })
      );
    } catch {
      /* ignore */
    }
  }, [version, index]);

  // Lightweight heartbeat every 5s (no await on response side-effects)
  useEffect(() => {
    const send = () => {
      const cur = media.length ? media[indexRef.current % media.length] : null;
      apiTV
        .post("/tv/heartbeat", {
          current_media_id: cur?.id || null,
          current_index: media.length ? indexRef.current % media.length : 0,
        })
        .catch((e) => {
          if (e?.response?.status === 401) {
            tokenStore.clearTV();
            navigate("/tv-login", { replace: true });
          }
        });
    };
    send();
    const id = setInterval(send, 5000);
    return () => clearInterval(id);
  }, [media, navigate]);

  // Slideshow timer for images.
  // Single-image playlists stay fixed (no fade-loop on the same content).
  const duration = playlist?.item_duration ? playlist.item_duration * 1000 : 8000;
  useEffect(() => {
    if (media.length <= 1) return; // 0 → empty stage; 1 → static image, no rotation
    const current = media[index % media.length];
    if (!current) return;
    const isImage =
      current.media_type === "image" ||
      /^image\//.test(current.content_type || "");
    if (isImage) {
      const t = setTimeout(() => setIndex((i) => (i + 1) % media.length), duration);
      return () => clearTimeout(t);
    }
  }, [index, media, duration]);

  // Preload next 2 images for buttery transitions (skipped when only one item).
  useEffect(() => {
    if (media.length <= 1) return;
    for (let offset = 1; offset <= 2; offset++) {
      const nextItem = media[(index + offset) % media.length];
      const isImage =
        nextItem &&
        (nextItem.media_type === "image" ||
          /^image\//.test(nextItem.content_type || ""));
      if (isImage) {
        const img = new Image();
        img.src = buildFileUrl(nextItem.id, "tv");
      }
    }
  }, [index, media]);

  // Try to enter fullscreen on first mount (often blocked unless triggered by a gesture)
  useEffect(() => {
    if (document.fullscreenElement) return;
    const el = document.documentElement;
    const req =
      el.requestFullscreen ||
      el.webkitRequestFullscreen ||
      el.mozRequestFullScreen ||
      el.msRequestFullscreen;
    if (req) {
      Promise.resolve(req.call(el)).catch(() => {});
    }
  }, []);

  // Overlay reveal on interaction
  const revealOverlay = useCallback(() => {
    setShowOverlay(true);
    if (overlayTimer.current) clearTimeout(overlayTimer.current);
    overlayTimer.current = setTimeout(() => setShowOverlay(false), 3000);
  }, []);

  useEffect(() => () => {
    if (overlayTimer.current) clearTimeout(overlayTimer.current);
  }, []);

  const current = useMemo(
    () => (media.length ? media[index % media.length] : null),
    [media, index]
  );

  const logout = async () => {
    if (document.fullscreenElement) {
      try {
        await document.exitFullscreen();
      } catch {
        /* ignore */
      }
    }
    tokenStore.clearTV();
    try {
      localStorage.removeItem("cc_tv_last_sync");
      localStorage.removeItem("cc_tv_position");
    } catch {
      /* ignore */
    }
    navigate("/tv-login", { replace: true });
  };

  if (loading) {
    return (
      <div className="fixed inset-0 grid place-items-center text-white bg-black" data-testid="tv-player-loading">
        <Loader2 className="h-8 w-8 animate-spin" strokeWidth={1.25} />
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black text-white overflow-hidden cursor-none"
      onMouseMove={revealOverlay}
      onClick={revealOverlay}
      onTouchStart={revealOverlay}
      data-testid="tv-player-page"
    >
      {!current ? (
        <EmptyStage hasPlaylist={!!playlist} location={tv?.location} nextSchedule={nextSchedule} pending={tv?.pending || !tv?.business_id} />
      ) : (
        <CrossfadeMedia
          item={current}
          buildSrc={(it) => buildFileUrl(it.id, "tv")}
          duration={2000}
          withBackdrop={true}
          onVideoEnded={() => setIndex((i) => (i + 1) % media.length)}
        />
      )}

      {/* Discreet overlay shown only on interaction */}
      <div
        className={`absolute top-0 inset-x-0 px-6 py-4 flex items-center justify-between transition-opacity duration-300 ${
          showOverlay ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="rounded-full bg-black/50 backdrop-blur-md px-4 py-1.5 text-xs uppercase tracking-[0.25em] text-white/80">
          {tv?.location} · {tv?.name}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={logout}
          className="border-white/20 bg-black/50 hover:bg-black/70 text-white backdrop-blur-md"
          data-testid="tv-logout-button"
        >
          <LogOut className="h-4 w-4 mr-2" strokeWidth={1.25} /> Disconnect
        </Button>
      </div>

      {error ? (
        <div
          className={`absolute bottom-6 left-1/2 -translate-x-1/2 text-xs text-red-300 transition-opacity ${
            showOverlay ? "opacity-100" : "opacity-0"
          }`}
          data-testid="tv-player-error"
        >
          {error}
        </div>
      ) : null}
    </div>
  );
}

function EmptyStage({ hasPlaylist, location, nextSchedule, pending }) {
  // PENDING device: just registered, admin hasn't claimed it yet.
  if (pending) {
    let uid = "";
    try { uid = (localStorage.getItem("cc_device_uid") || "").slice(0, 18); } catch { /* ignore */ }
    return (
      <div className="absolute inset-0 grid place-items-center px-8 text-center tv-stage">
        <div className="cc-fade-in max-w-xl">
          <div className="mx-auto h-16 w-16 rounded-2xl bg-amber-500/15 grid place-items-center text-amber-300 mb-6">
            <div className="h-3 w-3 rounded-full bg-amber-300 animate-pulse" />
          </div>
          <div className="font-heading text-3xl sm:text-5xl font-semibold tracking-tight leading-tight">
            Waiting to be claimed
          </div>
          <div className="mt-5 text-base text-white/70 max-w-md mx-auto leading-relaxed">
            This device is registered and online. Open <span className="text-white font-medium">CreatiCore Admin</span> → <span className="text-white font-medium">Devices</span> and click <span className="text-white font-medium">"Claim device"</span> to assign it to an account.
          </div>
          {uid && (
            <div className="mt-8 inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md px-4 py-2 text-xs font-mono text-white/80">
              uid: {uid}…
            </div>
          )}
        </div>
      </div>
    );
  }
  // Friendlier idle screen: if a schedule is upcoming, show what's coming next & when.
  if (nextSchedule) {
    const mins = nextSchedule.starts_in_minutes;
    let when;
    if (mins < 60) {
      when = `Empieza en ${mins} min · ${nextSchedule.starts_at_local}`;
    } else if (nextSchedule.is_today) {
      when = `Empieza en ${Math.round(mins / 60)} h · hoy a las ${nextSchedule.starts_at_local}`;
    } else {
      when = `Empieza ${nextSchedule.starts_on_day} a las ${nextSchedule.starts_at_local}`;
    }
    return (
      <div className="absolute inset-0 grid place-items-center px-8 text-center tv-stage">
        <div className="cc-fade-in max-w-xl">
          <div className="text-[10px] uppercase tracking-[0.35em] text-white/40 mb-4">
            {location || "Esta televisión"}
          </div>
          <div className="font-heading text-3xl sm:text-5xl font-semibold tracking-tight leading-tight">
            En pausa
          </div>
          <div className="mt-8 inline-flex items-center gap-3 rounded-full bg-white/10 backdrop-blur-md px-5 py-2.5">
            <span className="h-2 w-2 rounded-full bg-amber-300 animate-pulse" />
            <span className="text-sm text-white/80">
              <span className="text-white/60">Siguiente:</span>{" "}
              <span className="font-medium text-white">{nextSchedule.playlist_name}</span>
            </span>
          </div>
          <div className="mt-4 text-sm text-white/50">{when}</div>
        </div>
      </div>
    );
  }
  return (
    <div className="absolute inset-0 grid place-items-center px-8 text-center tv-stage">
      <div className="cc-fade-in">
        <div className="mx-auto h-16 w-16 rounded-2xl bg-white/10 grid place-items-center text-blue-200">
          <ImageIcon className="h-8 w-8" strokeWidth={1} />
        </div>
        <div className="mt-6 font-heading text-3xl sm:text-4xl font-semibold tracking-tight">
          {hasPlaylist ? "Playlist is empty" : "Waiting for content"}
        </div>
        <div className="mt-3 text-sm text-white/60 max-w-md mx-auto">
          {location ? `${location} · ` : ""}Ask the admin to publish content to this television.
        </div>
      </div>
    </div>
  );
}
